import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import {
    Table,
    TableHeader,
    TableRow,
    TableHead,
    TableBody,
    TableCell,
} from '@/components/ui/table'
import {
    SURVEY_CATEGORY_LABELS,
    UPSALE_SERVICE_MAP,
    UpsalePotentialValue,
} from '@/modules/Survey/constants'
import { SurveyRecordItem } from '@/modules/Survey/types'
import { getServiceColor } from '@/constants/serviceColors'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface SurveyRecordTableProps {
    surveyRecord: SurveyRecordItem
}

export const SurveyRecordTable = ({ surveyRecord }: SurveyRecordTableProps) => {
    const { t } = useTranslation('surveys')

    const categories = [
        {
            key: 'service_quality',
            score: surveyRecord.score_service_quality,
            note: surveyRecord.note_service_quality,
        },
        {
            key: 'customer_care',
            score: surveyRecord.score_customer_care,
            note: surveyRecord.note_customer_care,
        },
        {
            key: 'tech_support',
            score: surveyRecord.score_tech_support,
            note: surveyRecord.note_tech_support,
        },
        {
            key: 'issue_resolution',
            score: surveyRecord.score_issue_resolution,
            note: surveyRecord.note_issue_resolution,
        },
        {
            key: 'referral_willingness',
            score: surveyRecord.score_referral_willingness,
            note: surveyRecord.note_referral_willingness,
        },
    ]

    const extraNotes = [
        {
            labelKey: 'surveys.form.improvement',
            fallback: 'Improvement',
            value: surveyRecord.improvement,
            alwaysShow: true,
        },
        {
            labelKey: 'surveys.form.note_general',
            fallback: 'General Note',
            value: surveyRecord.note_general,
            alwaysShow: true,
        },
    ]

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle>
                    {t('surveys.responses.label', 'Responses')}
                </CardTitle>
                {surveyRecord.average_score && (
                    <div className="flex items-center gap-1.5 bg-background border px-2 py-0.5 rounded-sm shadow-sm">
                        <span className="text-[10px] uppercase font-bold text-muted-foreground">
                            {t('surveys.average_score', 'Avg')}:
                        </span>
                        <span className="text-sm font-bold text-primary">
                            {surveyRecord.average_score.toFixed(1)}
                        </span>
                    </div>
                )}
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                {t('surveys.column.category', 'Category')}
                            </TableHead>
                            <TableHead className="text-right w-[120px]">
                                {t('surveys.column.score', 'Score')}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {categories.map((item) => (
                            <TableRow key={item.key}>
                                <TableCell className="py-3">
                                    <p className="font-medium">
                                        {t(
                                            SURVEY_CATEGORY_LABELS[item.key] ||
                                                item.key
                                        )}
                                    </p>
                                    {item.note && (
                                        <p className="text-xs text-muted-foreground mt-1 italic">
                                            {item.note}
                                        </p>
                                    )}
                                </TableCell>
                                <TableCell className="text-right py-3 text-primary font-bold">
                                    {item.score ? (
                                        <div className="flex items-center justify-end gap-3">
                                            <div className="h-2 w-24 bg-muted rounded-full overflow-hidden hidden sm:block">
                                                <div
                                                    className={cn(
                                                        'h-full rounded-full',
                                                        item.score >= 8
                                                            ? 'bg-green-500'
                                                            : item.score >= 5
                                                              ? 'bg-yellow-500'
                                                              : 'bg-red-500'
                                                    )}
                                                    style={{
                                                        width: `${(item.score / 10) * 100}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="w-6 text-right">
                                                {item.score}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground text-xs italic">
                                            {t('surveys.not_rated', 'N/A')}
                                        </span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Upsale Potential */}
                <div className="pt-4">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase mb-2">
                        {t(
                            'surveys.column.upsale_potential',
                            'Upsale Potential'
                        )}
                    </h4>
                    <div className="flex flex-wrap gap-1">
                        {surveyRecord.upsale_potential &&
                        surveyRecord.upsale_potential.length > 0 ? (
                            surveyRecord.upsale_potential.map((v) => {
                                const sc = getServiceColor(
                                    UPSALE_SERVICE_MAP[
                                        v as UpsalePotentialValue
                                    ]
                                )
                                return (
                                    <span
                                        key={v}
                                        className="px-2 py-0.5 text-xs font-medium rounded"
                                        style={{
                                            backgroundColor: sc.bg,
                                            color: sc.text,
                                        }}
                                    >
                                        {v}
                                    </span>
                                )
                            })
                        ) : (
                            <span className="text-muted-foreground text-sm">
                                —
                            </span>
                        )}
                    </div>
                </div>

                {/* Extra notes: improvement, note_general */}
                {extraNotes
                    .filter((n) => n.alwaysShow || !!n.value)
                    .map((n) => (
                        <div key={n.labelKey} className="pt-4 border-t mt-4">
                            <h4 className="text-xs font-bold text-muted-foreground uppercase mb-2">
                                {t(n.labelKey, n.fallback)}
                            </h4>
                            {n.value ? (
                                <p className="text-sm text-slate-800 leading-relaxed">
                                    {n.value}
                                </p>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    —
                                </p>
                            )}
                        </div>
                    ))}
            </CardContent>
        </Card>
    )
}
